import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import StatusBar from './StatusBar';
import AlertsStrip from './AlertsStrip';
import RestRoutesTable from './RestRoutesTable';
import McpHealthTable from './McpHealthTable';
import McpHealthDrawer from './McpHealthDrawer';
import LiveEventFeed from './LiveEventFeed';
import type { AppObserveAggregate, McpEndpointRow } from './types';
import {
  useKongCatalog,
  useMcpAppStats,
  useMcpEndpoints,
  useObserveEventStream,
  useObserveStats24h,
  useOpenIncidents,
  useSloBreaches,
  useUptimeTimeline,
} from './useExposeHealth';
import { useSharedServiceHealth } from '@/lib/ServiceHealthContext';

export default function ExposeHealthTab() {
  const [streamPaused, setStreamPaused] = useState(false);
  const [drawerEp, setDrawerEp] = useState<McpEndpointRow | null>(null);
  const [drawerAgg, setDrawerAgg] = useState<AppObserveAggregate | undefined>();
  const [lastStatusFetch, setLastStatusFetch] = useState<number | null>(null);

  const { query: statusQuery, payload: statusPayload } = useSharedServiceHealth();
  const catalogQuery = useKongCatalog();
  const mcpQuery = useMcpEndpoints();
  const statsQuery = useObserveStats24h();
  const incidentsQuery = useOpenIncidents();
  const breachesQuery = useSloBreaches();
  const { events, hasSseUrl, streamOpen, streamError, timedOut, retry } =
    useObserveEventStream(!streamPaused);

  const connectionId = drawerAgg?.primary_connection_id ?? null;
  const uptimeQuery = useUptimeTimeline(connectionId);
  const appStatsQuery = useMcpAppStats(drawerEp?.app_id ?? null);

  React.useEffect(() => {
    if (statusQuery.dataUpdatedAt) setLastStatusFetch(statusQuery.dataUpdatedAt);
  }, [statusQuery.dataUpdatedAt]);

  const handleRowClick = (endpoint: McpEndpointRow, aggregate: AppObserveAggregate | undefined) => {
    setDrawerEp(endpoint);
    setDrawerAgg(aggregate);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Exposed REST routes use the Kong catalog plus /services/status probes. The table below that is a tenant-wide MCP last-call probe board — not this app&apos;s public endpoint health. Header Healthy(n/n) is process chrome and is unrelated to those rows.
      </Typography>

      <StatusBar
        data={statusPayload ?? undefined}
        isLoading={statusQuery.isLoading}
        error={statusQuery.error as Error | null}
        lastFetchedAt={lastStatusFetch}
      />

      <AlertsStrip
        incidents={incidentsQuery.data}
        breaches={breachesQuery.data}
        incidentsLoading={incidentsQuery.isLoading}
        breachesLoading={breachesQuery.isLoading}
        incidentsError={incidentsQuery.error as Error | null}
        breachesError={breachesQuery.error as Error | null}
      />

      <RestRoutesTable
        catalog={catalogQuery.data}
        status={statusPayload ?? undefined}
        isLoading={catalogQuery.isLoading}
        error={catalogQuery.error as Error | null}
      />

      <McpHealthTable
        endpoints={mcpQuery.data}
        observeStats={statsQuery.data?.stats}
        isLoading={mcpQuery.isLoading || statsQuery.isLoading}
        endpointsError={mcpQuery.error as Error | null}
        statsError={statsQuery.error as Error | null}
        onRowClick={handleRowClick}
      />

      <LiveEventFeed
        events={events}
        paused={streamPaused}
        hasSseUrl={hasSseUrl}
        streamOpen={streamOpen}
        streamError={streamError}
        timedOut={timedOut}
        onTogglePause={() => setStreamPaused((p) => !p)}
        onRetry={retry}
        endpoints={mcpQuery.data || []}
      />

      <McpHealthDrawer
        open={Boolean(drawerEp)}
        endpoint={drawerEp}
        connectionId={connectionId}
        uptime={uptimeQuery.data}
        uptimeLoading={uptimeQuery.isLoading}
        appStats={appStatsQuery.data}
        statsLoading={appStatsQuery.isLoading}
        toolStats={(drawerAgg?.tool_stats || []).map((t) => ({
          connection_id: t.connection_id,
          call_count: t.call_count,
          error_rate_pct: t.error_rate_pct,
          avg_latency_ms: t.avg_latency_ms,
        }))}
        onClose={() => {
          setDrawerEp(null);
          setDrawerAgg(undefined);
        }}
      />
    </Box>
  );
}
